import * as React from 'react';
import { MoreMenu, Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@components/atoms';
import { cn } from '@utils';

interface MenuOption {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	disabledReason?: string;
	dataTestId?: string;
}

interface BaseMenuProps {
	options: MenuOption[];
	trigger?: React.ReactNode;
	className?: string;
	menuBodyClassName?: string;
}

export const Menu: React.FC<BaseMenuProps> = ({ options, trigger, className, menuBodyClassName }: BaseMenuProps) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0 });
	const menuRef = React.useRef<HTMLDivElement>(null);

	const updateMenuPosition = React.useCallback(() => {
		const rect = menuRef.current?.getBoundingClientRect();
		if (rect) {
			setMenuPosition({ top: rect.top, left: rect.left });
		}
	}, []);

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (!isOpen) {
			updateMenuPosition();
		}
		setIsOpen(!isOpen);
	};

	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		updateMenuPosition();
		const events = ['scroll', 'resize'];
		events.forEach(event => window.addEventListener(event, updateMenuPosition, true));

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			events.forEach(event => window.removeEventListener(event, updateMenuPosition, true));
		};
	}, [updateMenuPosition]);

	return (
		<div ref={menuRef} className={cn('relative inline-block cursor-pointer text-left', className)}>
			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
			<div onClick={handleClick}>
				{trigger || (
					<button
						type="button"
						data-testid="menu-trigger"
						className="flex h-8 w-8 items-center justify-center text-secondary hover:text-primary"
					>
						<MoreMenu />
					</button>
				)}
			</div>

			{isOpen && (
				<MenuBody
					options={options}
					setIsOpen={setIsOpen}
					handleClick={handleClick}
					top={menuPosition.top}
					left={menuPosition.left}
					className={menuBodyClassName}
				/>
			)}
		</div>
	);
};

const MenuBody = ({
	options,
	handleClick,
	setIsOpen,
	top,
	left,
	className,
}: {
	options: MenuOption[];
	handleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	top?: number;
	left?: number;
	className?: string;
}) => {
	const mainMenuRef = React.useRef<HTMLDivElement>(null);
	const [menuOffset, setMenuOffset] = React.useState(0);
	const [verticalPosition, setVerticalPosition] = React.useState<'top' | 'bottom'>('bottom');

	React.useEffect(() => {
		const calculatePosition = () => {
			const offset = 10;
			const width = mainMenuRef.current?.clientWidth;
			const height = mainMenuRef.current?.clientHeight;

			if (!width) return;

			// Calculate horizontal offset to prevent overflow on the right
			const horizontalOffset = -width + offset;
			setMenuOffset(horizontalOffset);

			// Calculate vertical position to prevent overflow at bottom
			if (height && top !== undefined) {
				const viewportHeight = window.innerHeight;
				const menuBottomPosition = top + height + 8; // 8px is the top-2 offset
				const triggerHeight = 32; // Approximate height of the trigger button

				// If menu would overflow bottom, position it above the trigger
				if (menuBottomPosition > viewportHeight) {
					setVerticalPosition('top');
				} else {
					setVerticalPosition('bottom');
				}
			}
		};

		// Use requestAnimationFrame to ensure the menu is rendered before calculating
		requestAnimationFrame(calculatePosition);
	}, [top]);

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div
			className="fixed z-50"
			style={{ visibility: menuOffset ? 'visible' : 'hidden', top, left }}
			onClick={handleClick}
		>
			<div
				className={cn(
					'absolute z-50 rounded-md bg-white shadow-lg',
					verticalPosition === 'bottom' ? 'top-2' : 'bottom-2',
					className
				)}
				ref={mainMenuRef}
				style={{ left: menuOffset }}
			>
			{options.map((option: MenuOption, index: number) =>
				!option.disabledReason ? (
					<button
						key={index}
						onClick={() => {
							option.onClick();
							setIsOpen(false);
						}}
						className="block w-full px-4 py-2 text-left text-sm text-primary hover:bg-bgPrimary focus:outline-hidden"
						disabled={option.disabled}
						data-testid={option.dataTestId}
					>
						{option.label}
					</button>
				) : (
					<TooltipProvider key={index} delayDuration={300}>
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									disabled={true}
									className="block w-full cursor-not-allowed px-4 py-2 text-left text-sm text-gray-400"
									data-testid={option.dataTestId}
								>
									{option.label}
								</button>
							</TooltipTrigger>
							<TooltipContent>
								<p>{option.disabledReason}</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				)
			)}
			</div>
		</div>
	);
};
